// client/src/pages/School_Admin/Facilities/TransportRoutesPage.js
import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Dialog, DialogContent, DialogTitle, DialogActions,
  Chip, Snackbar, Alert, Paper, Grid, Card, CardContent,
  Tabs, Tab, IconButton, Tooltip, Avatar, Autocomplete,
  InputAdornment, LinearProgress
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  DirectionsBus as BusIcon, Route as RouteIcon, Search as SearchIcon,
  PersonAdd as PersonAddIcon, PersonRemove as PersonRemoveIcon,
  Groups as GroupsIcon
} from '@mui/icons-material';
import { fetchJson } from '../../../utils/fetchJson';

const emptyRouteForm = { name: '', description: '', startPoint: '', endPoint: '', stops: '', status: 'active' };
const emptyVehicleForm = { vehicleNumber: '', capacity: '', driverId: '', routeId: '', status: 'active' };

const TransportRoutesPage = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  // Routes
  const [routes, setRoutes] = useState([]);
  const [routeDialog, setRouteDialog] = useState(false);
  const [editingRouteId, setEditingRouteId] = useState(null);
  const [routeForm, setRouteForm] = useState({ ...emptyRouteForm });
  // Vehicles
  const [vehicles, setVehicles] = useState([]);
  const [vehicleDialog, setVehicleDialog] = useState(false);
  const [editingVehicleId, setEditingVehicleId] = useState(null);
  const [vehicleForm, setVehicleForm] = useState({ ...emptyVehicleForm });
  // Driver picker
  const [driverInput, setDriverInput] = useState('');
  const [driverOptions, setDriverOptions] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  // Allocations
  const [selectedRouteId, setSelectedRouteId] = useState('');
  const [allocations, setAllocations] = useState([]);
  const [allocLoading, setAllocLoading] = useState(false);
  // Student autocomplete
  const [studentInput, setStudentInput] = useState('');
  const [studentOptions, setStudentOptions] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  // General
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const toast = useCallback((message, severity = 'success') => setSnackbar({ open: true, message, severity }), []);

  // ─── Load Data ───
  const fetchRoutes = useCallback(async () => {
    try {
      const res = await fetchJson('/facilities/transport/routes', { method: 'GET' });
      setRoutes(Array.isArray(res) ? res : (res.routes || []));
    } catch (e) { toast('Failed to fetch routes', 'error'); }
  }, [toast]);

  const fetchVehicles = useCallback(async () => {
    try {
      const res = await fetchJson('/facilities/transport/vehicles', { method: 'GET' });
      setVehicles(Array.isArray(res) ? res : (res.vehicles || []));
    } catch (e) { toast('Failed to fetch vehicles', 'error'); }
  }, [toast]);

  useEffect(() => {
    Promise.all([fetchRoutes(), fetchVehicles()]).finally(() => setLoading(false));
  }, [fetchRoutes, fetchVehicles]);

  useEffect(() => {
    const refresh = () => { fetchRoutes(); fetchVehicles(); };
    window.addEventListener('app:ay:changed', refresh);
    window.addEventListener('app:phase:changed', refresh);
    return () => { window.removeEventListener('app:ay:changed', refresh); window.removeEventListener('app:phase:changed', refresh); };
  }, [fetchRoutes, fetchVehicles]);

  // ─── Routes CRUD ───
  const openAddRoute = () => { setEditingRouteId(null); setRouteForm({ ...emptyRouteForm }); setRouteDialog(true); };
  const openEditRoute = (r) => {
    setEditingRouteId(r.id);
    setRouteForm({
      name: r.name || '', description: r.description || '',
      startPoint: r.startPoint || '', endPoint: r.endPoint || '',
      stops: Array.isArray(r.stops) ? r.stops.join(', ') : (r.stops || ''),
      status: r.status || 'active'
    });
    setRouteDialog(true);
  };

  const saveRoute = async () => {
    if (!routeForm.name.trim()) { toast('Route name is required', 'warning'); return; }
    try {
      const stops = routeForm.stops ? routeForm.stops.split(',').map(s => s.trim()).filter(Boolean) : [];
      const payload = { ...routeForm, stops };
      if (editingRouteId) {
        await fetchJson(`/facilities/transport/routes/${editingRouteId}`, { method: 'PUT', body: payload });
        toast('Route updated');
      } else {
        await fetchJson('/facilities/transport/routes', { method: 'POST', body: payload });
        toast('Route created');
      }
      setRouteDialog(false);
      fetchRoutes();
    } catch (e) { toast(e?.message || 'Failed to save route', 'error'); }
  };

  // ─── Vehicles CRUD ───
  const openAddVehicle = () => {
    setEditingVehicleId(null);
    setVehicleForm({ ...emptyVehicleForm });
    setSelectedDriver(null); setDriverInput(''); setDriverOptions([]);
    setVehicleDialog(true);
  };
  const openEditVehicle = (v) => {
    setEditingVehicleId(v.id);
    setVehicleForm({
      vehicleNumber: v.vehicleNumber || '', capacity: v.capacity || '',
      driverId: v.driverId || '', routeId: v.routeId || '', status: v.status || 'active'
    });
    setSelectedDriver(v.driverId ? { id: v.driverId, label: v.driverName || `Driver #${v.driverId}` } : null);
    setDriverInput(''); setDriverOptions([]);
    setVehicleDialog(true);
  };

  const searchStaff = async (q) => {
    if (!q || q.length < 2) { setDriverOptions([]); return; }
    try {
      const res = await fetchJson(`/facilities/staff/search?q=${encodeURIComponent(q)}`, { method: 'GET' });
      const list = res.staff || [];
      setDriverOptions(list.map(u => ({ id: u.id, label: u.name, role: u.role })));
    } catch (_) { setDriverOptions([]); }
  };

  const saveVehicle = async () => {
    if (!vehicleForm.vehicleNumber.trim()) { toast('Vehicle number is required', 'warning'); return; }
    try {
      const payload = { ...vehicleForm, capacity: vehicleForm.capacity ? Number(vehicleForm.capacity) : null };
      if (editingVehicleId) {
        await fetchJson(`/facilities/transport/vehicles/${editingVehicleId}`, { method: 'PUT', body: payload });
        toast('Vehicle updated');
      } else {
        await fetchJson('/facilities/transport/vehicles', { method: 'POST', body: payload });
        toast('Vehicle created');
      }
      setVehicleDialog(false);
      fetchVehicles();
    } catch (e) { toast(e?.message || 'Failed to save vehicle', 'error'); }
  };

  // ─── Delete (shared) ───
  const handleDelete = async () => {
    if (!deleteConfirm) return;
    const { type, id, label } = deleteConfirm;
    try {
      if (type === 'route') {
        await fetchJson(`/facilities/transport/routes/${id}`, { method: 'DELETE' });
        fetchRoutes();
      } else if (type === 'vehicle') {
        await fetchJson(`/facilities/transport/vehicles/${id}`, { method: 'DELETE' });
        fetchVehicles();
      } else if (type === 'allocation') {
        await fetchJson(`/facilities/transport/allocations/${id}`, { method: 'DELETE' });
        loadAllocations(selectedRouteId);
      }
      toast(`${label || type} deleted`);
      setDeleteConfirm(null);
    } catch (e) { toast(e?.message || 'Delete failed', 'error'); }
  };

  // ─── Allocations ───
  const loadAllocations = async (routeId) => {
    if (!routeId) { setAllocations([]); return; }
    setAllocLoading(true);
    try {
      const res = await fetchJson(`/facilities/transport/routes/${routeId}/allocations`, { method: 'GET' });
      setAllocations(Array.isArray(res) ? res : (res.allocations || []));
    } catch (e) {
      toast('Failed to load allocations', 'error');
      setAllocations([]);
    } finally { setAllocLoading(false); }
  };

  useEffect(() => { if (tab === 2 && selectedRouteId) loadAllocations(selectedRouteId); }, [selectedRouteId, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAllocateStudent = async () => {
    if (!selectedStudent || !selectedRouteId) { toast('Select a student and a route', 'warning'); return; }
    try {
      await fetchJson('/facilities/transport/allocate', { method: 'POST', body: { studentId: selectedStudent.id, routeId: selectedRouteId } });
      toast('Student allocated successfully');
      setSelectedStudent(null);
      setStudentInput('');
      loadAllocations(selectedRouteId);
    } catch (e) { toast(e?.message || 'Allocation failed', 'error'); }
  };

  // ─── Student search ───
  const searchStudents = async (q) => {
    if (!q || q.length < 2) { setStudentOptions([]); return; }
    try {
      const res = await fetchJson(`/facilities/students/search?q=${encodeURIComponent(q)}`, { method: 'GET' });
      const list = res.students || [];
      setStudentOptions(list.map(s => ({
        id: s.id, label: s.name || `${s.firstName || ''} ${s.lastName || ''}`.trim(),
        photoUrl: s.photoUrl, studentNumber: s.studentNumber, className: s.className
      })));
    } catch (_) { setStudentOptions([]); }
  };

  // ─── Filtering ───
  const filteredRoutes = routes.filter(r => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (r.name || '').toLowerCase().includes(q) || (r.startPoint || '').toLowerCase().includes(q) || (r.endPoint || '').toLowerCase().includes(q);
  });

  const filteredVehicles = vehicles.filter(v => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (v.vehicleNumber || '').toLowerCase().includes(q) || (v.driverName || '').toLowerCase().includes(q);
  });

  const totalCapacity = vehicles.reduce((s, v) => s + (Number(v.capacity) || 0), 0);
  const activeVehicles = vehicles.filter(v => v.status === 'active').length;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <BusIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" fontWeight="bold">Transport Management</Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <Card><CardContent sx={{ textAlign: 'center' }}>
            <RouteIcon sx={{ fontSize: 28, color: 'primary.main', mb: 0.5 }} />
            <Typography color="text.secondary" variant="body2">Routes</Typography>
            <Typography variant="h4" fontWeight="bold">{routes.length}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card><CardContent sx={{ textAlign: 'center' }}>
            <BusIcon sx={{ fontSize: 28, color: 'success.main', mb: 0.5 }} />
            <Typography color="text.secondary" variant="body2">Active Vehicles</Typography>
            <Typography variant="h4" fontWeight="bold">{activeVehicles} / {vehicles.length}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card><CardContent sx={{ textAlign: 'center' }}>
            <GroupsIcon sx={{ fontSize: 28, color: 'info.main', mb: 0.5 }} />
            <Typography color="text.secondary" variant="body2">Total Capacity</Typography>
            <Typography variant="h4" fontWeight="bold">{totalCapacity}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card><CardContent sx={{ textAlign: 'center' }}>
            <PersonAddIcon sx={{ fontSize: 28, color: 'warning.main', mb: 0.5 }} />
            <Typography color="text.secondary" variant="body2">Allocations</Typography>
            <Typography variant="h4" fontWeight="bold">{allocations.length}</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Routes" />
        <Tab label="Vehicles" />
        <Tab label="Student Allocations" />
      </Tabs>

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      {/* ═══ ROUTES TAB ═══ */}
      {tab === 0 && (
        <Box>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" placeholder="Search routes..."
                  value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
              </Grid>
              <Grid item xs={12} md={6} display="flex" justifyContent="flex-end">
                <Button variant="contained" startIcon={<AddIcon />} onClick={openAddRoute}>Add Route</Button>
              </Grid>
            </Grid>
          </Paper>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell><b>Route Name</b></TableCell>
                  <TableCell><b>From</b></TableCell>
                  <TableCell><b>To</b></TableCell>
                  <TableCell><b>Stops</b></TableCell>
                  <TableCell align="center"><b>Status</b></TableCell>
                  <TableCell align="center"><b>Actions</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRoutes.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center"><Typography color="text.secondary" py={3}>No routes found</Typography></TableCell></TableRow>
                ) : filteredRoutes.map(r => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{r.name}</Typography>
                      {r.description && <Typography variant="caption" color="text.secondary">{r.description}</Typography>}
                    </TableCell>
                    <TableCell>{r.startPoint || '-'}</TableCell>
                    <TableCell>{r.endPoint || '-'}</TableCell>
                    <TableCell>
                      {(Array.isArray(r.stops) ? r.stops : []).map((s, i) => (
                        <Chip key={i} label={s} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                      ))}
                      {(!r.stops || (Array.isArray(r.stops) && r.stops.length === 0)) && '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={r.status || 'active'} size="small" color={r.status === 'active' ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => openEditRoute(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteConfirm({ type: 'route', id: r.id, label: r.name })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ═══ VEHICLES TAB ═══ */}
      {tab === 1 && (
        <Box>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" placeholder="Search vehicles..."
                  value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
              </Grid>
              <Grid item xs={12} md={6} display="flex" justifyContent="flex-end">
                <Button variant="contained" startIcon={<AddIcon />} onClick={openAddVehicle}>Add Vehicle</Button>
              </Grid>
            </Grid>
          </Paper>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell><b>Vehicle No.</b></TableCell>
                  <TableCell><b>Capacity</b></TableCell>
                  <TableCell><b>Driver</b></TableCell>
                  <TableCell><b>Route</b></TableCell>
                  <TableCell align="center"><b>Status</b></TableCell>
                  <TableCell align="center"><b>Actions</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredVehicles.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center"><Typography color="text.secondary" py={3}>No vehicles found</Typography></TableCell></TableRow>
                ) : filteredVehicles.map(v => (
                  <TableRow key={v.id}>
                    <TableCell><Typography variant="body2" fontWeight={500}>{v.vehicleNumber}</Typography></TableCell>
                    <TableCell>{v.capacity || '-'}</TableCell>
                    <TableCell>{v.driverName || '-'}</TableCell>
                    <TableCell>{v.routeName || (routes.find(r => r.id === v.routeId)?.name) || '-'}</TableCell>
                    <TableCell align="center">
                      <Chip label={v.status || 'active'} size="small"
                        color={v.status === 'active' ? 'success' : v.status === 'maintenance' ? 'warning' : 'default'} />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => openEditVehicle(v)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteConfirm({ type: 'vehicle', id: v.id, label: v.vehicleNumber })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ═══ ALLOCATIONS TAB ═══ */}
      {tab === 2 && (
        <Box>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={1.5}>Allocate Student to Route</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={studentOptions}
                  getOptionLabel={(o) => o?.label || ''}
                  isOptionEqualToValue={(o, v) => o?.id === v?.id}
                  value={selectedStudent}
                  onChange={(e, val) => setSelectedStudent(val)}
                  inputValue={studentInput}
                  onInputChange={(e, v) => { setStudentInput(v); searchStudents(v); }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar src={option.photoUrl || ''} sx={{ width: 28, height: 28 }}>{(option.label || '').charAt(0)}</Avatar>
                        <Box>
                          <Typography variant="body2">{option.label}</Typography>
                          <Typography variant="caption" color="text.secondary">{option.className}{option.studentNumber ? ` — ${option.studentNumber}` : ''}</Typography>
                        </Box>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => <TextField {...params} label="Student" placeholder="Search by name..." size="small" />}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Route</InputLabel>
                  <Select label="Route" value={selectedRouteId} onChange={(e) => setSelectedRouteId(e.target.value)}>
                    <MenuItem value=""><em>Select route</em></MenuItem>
                    {routes.map(r => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button variant="contained" startIcon={<PersonAddIcon />} onClick={handleAllocateStudent} fullWidth>Allocate</Button>
              </Grid>
            </Grid>
          </Paper>

          {selectedRouteId && (
            <>
              <Typography variant="subtitle2" mb={1}>
                Students on: <b>{routes.find(r => r.id === Number(selectedRouteId) || r.id === selectedRouteId)?.name || 'Selected Route'}</b>
                ({allocations.length} allocated)
              </Typography>
              {allocLoading && <LinearProgress sx={{ mb: 1 }} />}
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell><b>Student</b></TableCell>
                      <TableCell><b>Student No.</b></TableCell>
                      <TableCell><b>Class</b></TableCell>
                      <TableCell><b>Allocated</b></TableCell>
                      <TableCell align="center"><b>Actions</b></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allocations.length === 0 ? (
                      <TableRow><TableCell colSpan={5} align="center"><Typography color="text.secondary" py={3}>No students allocated to this route</Typography></TableCell></TableRow>
                    ) : allocations.map(a => (
                      <TableRow key={a.id}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Avatar src={a.studentPhoto || ''} sx={{ width: 32, height: 32 }}>
                              {(a.studentName || '').charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="body2">{a.studentName || '-'}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{a.studentNumber || '-'}</TableCell>
                        <TableCell>{a.className || '-'}</TableCell>
                        <TableCell>{a.createdAt ? new Date(a.createdAt).toLocaleDateString() : '-'}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Remove allocation">
                            <IconButton size="small" color="error"
                              onClick={() => setDeleteConfirm({ type: 'allocation', id: a.id, label: `${a.studentName || 'Student'} allocation` })}>
                              <PersonRemoveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
          {!selectedRouteId && (
            <Alert severity="info" sx={{ mt: 1 }}>Select a route above to view and manage student allocations.</Alert>
          )}
        </Box>
      )}

      {/* Route Dialog */}
      <Dialog open={routeDialog} onClose={() => setRouteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRouteId ? 'Edit Route' : 'Add Route'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}><TextField label="Route Name" value={routeForm.name} onChange={(e) => setRouteForm(f => ({ ...f, name: e.target.value }))} fullWidth required /></Grid>
            <Grid item xs={12}><TextField label="Description" value={routeForm.description} onChange={(e) => setRouteForm(f => ({ ...f, description: e.target.value }))} fullWidth multiline rows={2} /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Start Point" value={routeForm.startPoint} onChange={(e) => setRouteForm(f => ({ ...f, startPoint: e.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} sm={6}><TextField label="End Point" value={routeForm.endPoint} onChange={(e) => setRouteForm(f => ({ ...f, endPoint: e.target.value }))} fullWidth /></Grid>
            <Grid item xs={12}><TextField label="Stops (comma separated)" value={routeForm.stops} onChange={(e) => setRouteForm(f => ({ ...f, stops: e.target.value }))} fullWidth multiline rows={2} placeholder="Stop A, Stop B, Stop C" /></Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={routeForm.status} onChange={(e) => setRouteForm(f => ({ ...f, status: e.target.value }))}>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRouteDialog(false)} variant="outlined">Cancel</Button>
          <Button variant="contained" onClick={saveRoute}>{editingRouteId ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      {/* Vehicle Dialog */}
      <Dialog open={vehicleDialog} onClose={() => setVehicleDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingVehicleId ? 'Edit Vehicle' : 'Add Vehicle'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}><TextField label="Vehicle Number" value={vehicleForm.vehicleNumber} onChange={(e) => setVehicleForm(f => ({ ...f, vehicleNumber: e.target.value }))} fullWidth required /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Capacity" type="number" value={vehicleForm.capacity} onChange={(e) => setVehicleForm(f => ({ ...f, capacity: e.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={driverOptions}
                getOptionLabel={(o) => o?.label || ''}
                isOptionEqualToValue={(o, v) => o?.id === v?.id}
                value={selectedDriver}
                onChange={(e, val) => { setSelectedDriver(val); setVehicleForm(f => ({ ...f, driverId: val?.id || '' })); }}
                inputValue={driverInput}
                onInputChange={(e, v) => { setDriverInput(v); searchStaff(v); }}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2">{option.label}</Typography>
                      {option.role && <Typography variant="caption" color="text.secondary">{option.role}</Typography>}
                    </Box>
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label="Driver" placeholder="Search staff..." fullWidth />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Route</InputLabel>
                <Select label="Route" value={vehicleForm.routeId} onChange={(e) => setVehicleForm(f => ({ ...f, routeId: e.target.value }))}>
                  <MenuItem value=""><em>None</em></MenuItem>
                  {routes.map(r => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={vehicleForm.status} onChange={(e) => setVehicleForm(f => ({ ...f, status: e.target.value }))}>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVehicleDialog(false)} variant="outlined">Cancel</Button>
          <Button variant="contained" onClick={saveVehicle}>{editingVehicleId ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent><Typography>Are you sure you want to delete <b>{deleteConfirm?.label}</b>?</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} variant="outlined">Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default TransportRoutesPage;
